import { atom } from "jotai";
import { createDerivedWritableAtom } from "../../utils";
import { VideoSDKAdapter } from "./adapter";
import { DEFAULT_LOCAL_CONTEXT_VALUE } from "./constants";
import { RejoinState, ThumbnailPosition, type VideoSDKAtom } from "./types";
import { getStoredAudioVideoSettings } from "./utils";

export class AdapterNotInitializedError extends Error {
  constructor(message: string = "Video SDK adapter has not been initialized") {
    super(message);
    this.name = "AdapterNotInitializedError";
  }
}

export const baseVideoSDKAdapterAtom = atom<VideoSDKAdapter | null>(null);

export const videoSDKAdapterAtom = atom(
  (get) => {
    const adapter = get(baseVideoSDKAdapterAtom);

    if (adapter === null) {
      throw new AdapterNotInitializedError();
    }

    return adapter;
  },
  (_get, set, adapter: VideoSDKAdapter | null) => {
    set(baseVideoSDKAdapterAtom, adapter);
  },
);

export const isVirtualBackgroundSupportedAtom = atom<boolean | undefined>(
  (get) => get(baseVideoSDKAdapterAtom)?.isVirtualBackgroundSupported,
);

// this should be in constants, but it invokes a function for a default value
export const DEFAULT_VIDEO_SDK_ATOM_VALUE = {
  availableDevices: null,
  connectedAt: undefined,
  currentCameraId: undefined,
  currentMicrophoneId: undefined,
  currentOutputDeviceId: undefined,
  dominantSpeakerId: undefined,
  hasBackgroundProcessorFailure: false,
  isAudioPlaybackRestricted: undefined,
  isShowingScreenShareConfirmation: false,
  rejoinState: null,
  localAudioContext: DEFAULT_LOCAL_CONTEXT_VALUE,
  localAudioContextStats: null,
  localDataContext: DEFAULT_LOCAL_CONTEXT_VALUE,
  localScreenShareContext: DEFAULT_LOCAL_CONTEXT_VALUE,
  localVideoContext: DEFAULT_LOCAL_CONTEXT_VALUE,
  participants: [],
  pictureInPictureParticipantId: undefined,
  remoteAudioTrackStatsBySid: {},
  remoteScreenShareAudioDelegator: undefined,
  remoteScreenShareParticipant: undefined,
  remoteScreenShareVideoDelegator: undefined,
  remoteMediaByParticipantId: {},
  remoteMediaStatusByParticipantId: {},
  remoteParticipantConnectionStatesByParticipantId: {},
  room: undefined,
  roomToken: undefined,
  shouldOverrideSuggestedRoomVideoQuality: false,
  shouldTriggerPictureInPicture: false,
  thumbnailPosition: ThumbnailPosition.TOP_RIGHT,
  twilioIdentity: undefined,
  usedTwilioWebsocketsSuccessfully: false,
  userDataByParticipantId: {},
  usingWebsocketsReverseProxy: false,
  videoQuality: getStoredAudioVideoSettings().videoQualityMode,
  videoWasOnAtScreenLeave: null,
  zoomToken: undefined,
};

export const videoSDKAtom = atom<VideoSDKAtom>(DEFAULT_VIDEO_SDK_ATOM_VALUE);

export const roomAtom = createDerivedWritableAtom(videoSDKAtom, "room");

export const roomTokenAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "roomToken",
);

export const connectedAtAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "connectedAt",
);

export const hasBackgroundProcessorFailureAtom = atom(false);

export const currentOutputDeviceIdAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "currentOutputDeviceId",
);

type RejoinStateTransition = "rejoin" | "stop" | "close" | "open";
type RejoinFSMState = RejoinState | "start";

const rejoinStateFSM: {
  [state in RejoinFSMState]: {
    [transition in RejoinStateTransition]?: RejoinFSMState;
  };
} = {
  start: {
    open: RejoinState.Prompting,
  },
  [RejoinState.Prompting]: {
    rejoin: RejoinState.Rejoining,
    close: "start",
  },
  [RejoinState.Rejoining]: {
    stop: RejoinState.Prompting,
    close: "start",
  },
};

function transitionRejoinState(
  currentState: VideoSDKAtom["rejoinState"],
  transition: RejoinStateTransition,
): VideoSDKAtom["rejoinState"] {
  const toFSMState = (state: VideoSDKAtom["rejoinState"]): RejoinFSMState =>
    state === null ? "start" : state;

  const toAtomState = (state: RejoinFSMState): VideoSDKAtom["rejoinState"] =>
    state === "start" ? null : state;

  const fromState = toFSMState(currentState);
  const nextState = rejoinStateFSM[fromState][transition] ?? fromState;
  return toAtomState(nextState);
}

export const rejoinStateAtom = atom(
  (get) => get(videoSDKAtom).rejoinState,
  (
    get,
    set,
    {
      transition,
      state,
    }: {
      state?: VideoSDKAtom["rejoinState"];
      transition?: RejoinStateTransition;
    },
  ) => {
    if (state) {
      set(videoSDKAtom, (prev) => ({ ...prev, rejoinState: state }));
    }
    if (!transition) return;
    const currentState = get(rejoinStateAtom);
    const nextState = transitionRejoinState(currentState, transition);
    if (nextState === currentState) return;
    set(videoSDKAtom, (prev) => ({ ...prev, rejoinState: nextState }));
  },
);

export const isRejoinPromptOpenAtom = atom(
  (get) => !!get(rejoinStateAtom),
  (_get, set, value: boolean) => {
    set(rejoinStateAtom, {
      transition: value ? "open" : "close",
    });
  },
);

export const isRejoiningAtom = atom(
  (get) => get(rejoinStateAtom) === RejoinState.Rejoining,
  (_get, set, value: boolean) => {
    set(rejoinStateAtom, {
      transition: value ? "rejoin" : "stop",
    });
  },
);
